import { mediaItems, cardItems, rankingItems } from '@/mocks/homeData';

function SectionTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="text-center mb-10 md:mb-14">
      <h2 className="text-2xl md:text-3xl font-black text-foreground-950 tracking-tight">
        {title}
      </h2>
      <p className="mt-3 text-sm md:text-base text-foreground-500">{subtitle}</p>
    </div>
  );
}

export function MediaSection() {
  return (
    <section id="media" className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="mx-auto max-w-[1200px]">
        <SectionTitle
          title="精彩视频"
          subtitle="精选热门影视内容，高清画质畅快观影"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {mediaItems.map((item) => (
            <a
              key={item.id}
              href="#cards"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#cards')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="group block bg-background-200/80 rounded-xl border border-background-400/30 hover:border-primary-500/30 overflow-hidden transition-all duration-300 cursor-pointer hover:-translate-y-1"
            >
              <div className="relative w-full aspect-video overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50/80 via-transparent to-transparent"></div>
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <span className="px-2 py-1 rounded bg-background-50/70 text-foreground-800 text-xs font-mono whitespace-nowrap">
                    {item.duration}
                  </span>
                  <span className="text-xs text-foreground-500 flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-eye-line"></i>
                    {item.views}
                  </span>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-primary-500/85 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-play-fill text-foreground-950 text-2xl ml-0.5"></i>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-sm md:text-base font-bold text-foreground-900 group-hover:text-primary-400 transition-colors line-clamp-1">
                  {item.title}
                </h3>
                <p className="mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2">
                  {item.desc}
                </p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CardsSection() {
  return (
    <section id="cards" className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="mx-auto max-w-[1400px]">
        <SectionTitle
          title="热门推荐"
          subtitle="编辑精选高分佳作，口碑与热度双重保障"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cardItems.map((item) => (
            <a
              key={item.id}
              href="#media"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#media')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="group block bg-background-200/80 rounded-xl border border-background-400/30 hover:border-primary-500/30 overflow-hidden transition-all duration-300 cursor-pointer hover:-translate-y-1"
            >
              <div className="relative w-full aspect-[3/2] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-2.5 py-1 rounded-full bg-accent-500/90 text-background-950 text-xs font-semibold whitespace-nowrap">
                    {item.tag}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-sm md:text-base font-bold text-foreground-900 group-hover:text-primary-400 transition-colors line-clamp-1">
                  {item.title}
                </h3>
                <p className="mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2">
                  {item.desc}
                </p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function RankingSection() {
  const getChangeIcon = (change: string) => {
    if (change === 'up') return { icon: 'ri-arrow-up-s-line', color: 'text-primary-400' };
    if (change === 'down') return { icon: 'ri-arrow-down-s-line', color: 'text-accent-400' };
    if (change === 'new') return { icon: 'ri-sparkling-line', color: 'text-secondary-400' };
    return { icon: 'ri-subtract-line', color: 'text-foreground-500' };
  };

  const getRankBadge = (rank: number) => {
    if (rank === 1) return 'bg-primary-500 text-foreground-950';
    if (rank === 2) return 'bg-primary-400/80 text-foreground-950';
    if (rank === 3) return 'bg-primary-300/80 text-foreground-950';
    return 'bg-background-400/50 text-foreground-600';
  };

  return (
    <section id="ranking" className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="mx-auto max-w-[900px]">
        <SectionTitle
          title="热播榜单"
          subtitle="实时热度排行，看看大家都在看什么"
        />
        <div className="bg-background-200/80 rounded-xl border border-background-400/30 overflow-hidden">
          {rankingItems.map((item, idx) => {
            const changeInfo = getChangeIcon(item.change);
            return (
              <a
                key={item.id}
                href="#cards"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#cards')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className={`flex items-center gap-4 px-5 py-4 hover:bg-background-300/50 transition-colors duration-200 cursor-pointer ${
                  idx < rankingItems.length - 1 ? 'border-b border-background-400/20' : ''
                }`}
              >
                <span
                  className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${getRankBadge(
                    item.rank
                  )}`}
                >
                  {item.rank}
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground-800 truncate">
                    {item.title}
                  </h3>
                  <span className="text-xs text-foreground-500">{item.type}</span>
                </div>
                <span className="text-xs font-bold text-foreground-800 font-mono whitespace-nowrap">
                  {item.hot}
                </span>
                <span className={`flex-shrink-0 ${changeInfo.color}`}>
                  <i className={`${changeInfo.icon} text-sm`}></i>
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}